import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Phone, Mail, Clock, X } from 'lucide-react'
import { apiClient, config } from '@workspace/public-utils'
import { Seo } from '@workspace/public-ui/components/Seo'
import useCompanyInfo from '../hooks/useCompanyInfo'

interface PlanTier {
  id: string
  name: string
  price: number
  max_numbers_per_month: number
  features: string[]
  description?: string
  sold_out_mark?: string | null
  is_disabled?: boolean
  top_tier_comment?: string | null
  icon_image_url?: string | null
}

export default function LottomdSubscriptionPlansPage() {
  const dialog = useRef<HTMLDialogElement>(null)
  const {
    data: company,
    isLoading: companyLoading,
    isError: companyError,
    refetch: reloadCompany,
  } = useCompanyInfo()
  const {
    data: tiers,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['subscriptions', 'public'],
    queryFn: async ({ signal }) =>
      (await apiClient.get<PlanTier[]>('/api/v1/public/subscription-tiers', { signal })).data,
    staleTime: 30 * 60 * 1000,
  })
  const hasContacts =
    company?.contact_number || company?.contact_number_secondary || company?.contact_email

  return (
    <div className="md-plans">
      <Seo
        title="구독 안내"
        description="로또명당의 구독 플랜을 살펴보고 나에게 맞는 번호 서비스를 만나보세요."
        canonicalPath="/plans"
      />
      <section className="md-plans-hero">
        <span className="md-kicker">한 주의 기대를 함께 준비해요</span>
        <h1>
          나에게 맞는,
          <br />
          명당의 번호 서비스.
        </h1>
        <p>
          제공 내용을 차분히 살펴보세요.
          <br />
          궁금한 점은 상담으로 안내해 드릴게요.
        </p>
      </section>
      <div className="md-container md-content-width md-plan-list">
        {isLoading || isError || !tiers?.length ? (
          <div className="md-empty" role="status">
            <p>
              {isLoading
                ? '구독 플랜을 불러오고 있어요.'
                : isError
                  ? '구독 플랜을 불러오지 못했어요.'
                  : '구독 플랜을 준비하고 있어요.'}
            </p>
            {isError && (
              <button
                className="md-button md-button-outline"
                onClick={() => {
                  void refetch()
                }}
              >
                다시 불러오기
              </button>
            )}
          </div>
        ) : (
          [...tiers]
            .sort((a, b) => a.price - b.price)
            .map((tier) => (
              <article className="md-plan-card" key={tier.id}>
                <header>
                  {tier.icon_image_url && (
                    <img
                      className="md-plan-icon"
                      src={
                        /^(https?:)?\/\//.test(tier.icon_image_url)
                          ? tier.icon_image_url
                          : `${config.API_BASE_URL}${tier.icon_image_url}`
                      }
                      alt=""
                      width="40"
                      height="40"
                    />
                  )}
                  <h2>{tier.name}</h2>
                  {tier.top_tier_comment && (
                    <span className="md-plan-comment">{tier.top_tier_comment}</span>
                  )}
                  {tier.sold_out_mark && <span className="md-badge">{tier.sold_out_mark}</span>}
                </header>
                <div className="md-plan-body">
                  <div className="md-plan-quota">
                    <strong>{tier.max_numbers_per_month.toLocaleString()}회</strong>
                    <span>월 제공 번호 생성 횟수</span>
                  </div>
                  <ul className="md-plan-features">
                    {(tier.features.length ? tier.features : ['기본 번호 서비스 제공']).map(
                      (feature) => (
                        <li key={feature}>
                          <Check size={16} aria-hidden="true" />
                          {feature}
                        </li>
                      ),
                    )}
                  </ul>
                  <button
                    className="md-button"
                    type="button"
                    disabled={tier.is_disabled}
                    onClick={() => dialog.current?.showModal()}
                  >
                    {tier.is_disabled ? tier.sold_out_mark || '이용 불가' : '상담신청'}
                  </button>
                </div>
                {tier.description && <p className="md-plan-description">{tier.description}</p>}
              </article>
            ))
        )}
      </div>
      <dialog
        className="md-dialog"
        ref={dialog}
        aria-labelledby="consult-title"
        onClick={(event) => {
          if (event.target === event.currentTarget) dialog.current?.close()
        }}
      >
        <header>
          <h2 id="consult-title">플랜 가입 문의</h2>
          <button
            type="button"
            className="md-icon-button"
            aria-label="상담 안내 닫기"
            onClick={() => dialog.current?.close()}
          >
            <X size={20} />
          </button>
        </header>
        <div className="md-dialog-body">
          <p>
            편하신 방법으로 연락해 주세요.
            <br />
            가입과 이용 방법을 안내해 드릴게요.
          </p>
          {company?.contact_number && (
            <a
              className="md-contact-link"
              href={`tel:${company.contact_number.replace(/[^0-9+]/g, '')}`}
            >
              <Phone size={20} />
              <span>
                <small>대표 연락처</small>
                <strong>{company.contact_number}</strong>
              </span>
            </a>
          )}
          {company?.contact_number_secondary && (
            <a
              className="md-contact-link"
              href={`tel:${company.contact_number_secondary.replace(/[^0-9+]/g, '')}`}
            >
              <Phone size={20} />
              <span>
                <small>고객센터</small>
                <strong>{company.contact_number_secondary}</strong>
              </span>
            </a>
          )}
          {company?.contact_email && (
            <a className="md-contact-link" href={`mailto:${company.contact_email}`}>
              <Mail size={20} />
              <span>
                <small>이메일</small>
                <strong>{company.contact_email}</strong>
              </span>
            </a>
          )}
          {company?.working_hours && (
            <div className="md-contact-link">
              <Clock size={20} />
              <span>
                <small>상담 가능 시간</small>
                <strong>{company.working_hours}</strong>
              </span>
            </div>
          )}
          {!hasContacts && (
            <p role="status">
              {companyLoading
                ? '상담 연락처를 불러오는 중이에요.'
                : companyError
                  ? '연락처를 불러오지 못했어요.'
                  : '등록된 상담 연락처가 없어요.'}
            </p>
          )}
          {companyError && (
            <button
              className="md-text-link"
              onClick={() => {
                void reloadCompany()
              }}
            >
              연락처 다시 불러오기
            </button>
          )}
          <button className="md-button" type="button" onClick={() => dialog.current?.close()}>
            닫기
          </button>
        </div>
      </dialog>
    </div>
  )
}
